<template>
  <div v-bind="$attrs" :aria-hidden="isAriaHidden" :style="wrapperStyle">
    <div ref="contentEl" :class="contentClass" :style="contentStyle">
      <slot />
    </div>
  </div>
</template>
<script setup lang="ts">
// Animuje výšku obsahu přes CSS transition (náhrada za balíček vue-animate-height).
// Výška se drží v inline stylu, aby ji šlo přebít utility třídou s "!" (např. "@md:!h-max").

import type { CSSProperties } from 'vue'

type Height = number | string

const props = withDefaults(
  defineProps<{
    // Cílová výška: číslo v px, "auto" nebo procenta ("50%")
    height: Height
    duration?: number
    delay?: number
    easing?: string
    // Třída pro vnitřní (měřený) element – nesmí měnit display/height
    contentClass?: string
    animateOpacity?: boolean
    ariaHidden?: boolean
  }>(),
  {
    duration: 250,
    delay: 0,
    easing: 'ease',
    contentClass: undefined,
    animateOpacity: false,
    ariaHidden: undefined,
  }
)

const emit = defineEmits<{
  animationStart: [payload: { newHeight: Height }]
  animationEnd: [payload: { newHeight: Height }]
}>()

defineOptions({ inheritAttrs: false })

const isNumber = (value: unknown) => {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && !Number.isNaN(n) && Number.isFinite(n)
}

const isPercentage = (value: unknown): value is string =>
  typeof value === 'string' && value.endsWith('%') && isNumber(value.slice(0, -1))

const normalizeHeight = (value: Height): Height => {
  if (isNumber(value)) return Math.max(0, Number(value))
  if (isPercentage(value)) return value === '0%' ? 0 : value
  return 'auto'
}

const initialHeight = normalizeHeight(props.height)

const contentEl = ref<HTMLElement | null>(null)
// Aktuálně vykreslená výška – při změně propu je to zároveň výchozí bod animace
const currentHeight = ref<Height>(initialHeight)
const currentOverflow = ref<string | undefined>(initialHeight === 'auto' ? 'visible' : 'hidden')
const useTransition = ref(false)
// Schovaný obsah (height 0) se nedá tabovat ani číst čtečkou
const contentHidden = ref(initialHeight === 0)

let timeoutId: ReturnType<typeof setTimeout> | undefined
let frameIds: number[] = []

const getTimings = () => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  return prefersReducedMotion
    ? { delay: 0, duration: 0 }
    : { delay: props.delay, duration: props.duration }
}

const isAriaHidden = computed(() => props.ariaHidden ?? currentHeight.value === 0)

const wrapperStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    height:
      typeof currentHeight.value === 'number' ? `${currentHeight.value}px` : currentHeight.value,
    overflow: currentOverflow.value,
  }

  if (useTransition.value) {
    const { delay, duration } = getTimings()
    style.transition = `height ${duration}ms ${props.easing} ${delay}ms`
  }

  return style
})

const contentStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {}

  if (contentHidden.value) style.display = 'none'

  if (props.animateOpacity) {
    const { delay, duration } = getTimings()
    style.transition = `opacity ${duration}ms ${props.easing} ${delay}ms`
    if (currentHeight.value === 0) style.opacity = 0
  }

  return style
})

const clearPendingAnimation = () => {
  frameIds.forEach((id) => cancelAnimationFrame(id))
  frameIds = []
  clearTimeout(timeoutId)
  timeoutId = undefined
}

// Dvojitý rAF – první snímek propíše fixní výšku do DOM, druhý teprve spustí transition
const nextFrames = (callback: () => void) => {
  frameIds = []
  frameIds[0] = requestAnimationFrame(() => {
    frameIds[1] = requestAnimationFrame(callback)
  })
}

watch(
  () => props.height,
  (heightProp) => {
    const el = contentEl.value
    // Ref je null, pokud se komponenta mezitím odmountovala
    if (!el) return

    clearPendingAnimation()

    // Obsah musí být na okamžik viditelný, aby šel změřit
    contentHidden.value = false
    el.style.display = ''

    const prevContentOverflow = el.style.overflow
    el.style.overflow = 'hidden'
    const contentHeight = el.offsetHeight
    el.style.overflow = prevContentOverflow

    const startsFromAuto = currentHeight.value === 'auto'
    const targetHeight = normalizeHeight(heightProp)
    // Na "auto" se nedá animovat – jede se na změřenou výšku a ta se pak přepne zpět na auto
    const finalOverflow = targetHeight === 'auto' ? undefined : 'hidden'
    const animateTo = targetHeight === 'auto' ? contentHeight : targetHeight

    const { delay, duration } = getTimings()
    const totalDuration = duration + delay

    const finish = () => {
      currentHeight.value = targetHeight
      currentOverflow.value = finalOverflow
      useTransition.value = false
      contentHidden.value = targetHeight === 0
      emit('animationEnd', { newHeight: animateTo })
    }

    if (totalDuration === 0) {
      emit('animationStart', { newHeight: animateTo })
      finish()
      return
    }

    if (startsFromAuto) {
      // Nejdřív fixní výška bez transition, jinak není z čeho animovat
      currentHeight.value = contentHeight
      currentOverflow.value = 'hidden'
      useTransition.value = false

      nextFrames(() => {
        currentHeight.value = animateTo
        useTransition.value = true
        emit('animationStart', { newHeight: animateTo })
      })
    } else {
      currentHeight.value = animateTo
      currentOverflow.value = 'hidden'
      useTransition.value = true
      emit('animationStart', { newHeight: animateTo })
    }

    timeoutId = setTimeout(finish, totalDuration)
  },
  { flush: 'post' }
)

onBeforeUnmount(clearPendingAnimation)
</script>
